/**
 * Normalized Criteria Index Component
 * Displays evaluation criteria from V3 RFP extraction with quality scoring
 */
import React, { useState, useMemo } from 'react';
import { Card, Input, Button } from '@tracekit/core';
import { Search, Download } from 'lucide-react';
import type { RfpEvaluationCriterion, RfpRequirement } from '../../types/rfp';

export interface NormalizedCriteriaIndexProps {
  // V3 extraction props (NEW)
  evaluationCriteria?: RfpEvaluationCriterion[];
  requirements?: RfpRequirement[];
  
  // Legacy props (for backward compatibility)
  criteria?: RfpEvaluationCriterion[];
  criteriaIndex?: any;
  gateCriteria?: any[];
  scoredCriteria?: any[];
  
  qualityScore?: number;
  onCriterionClick?: (criterion: RfpEvaluationCriterion) => void;
  loading?: boolean;
}

type CriterionType = 'technical' | 'financial' | 'experience' | 'resources' | 'other';
type SortField = 'title' | 'type' | 'weight' | 'points' | 'page';
type SortDirection = 'asc' | 'desc';

export function NormalizedCriteriaIndex({
  evaluationCriteria,
  requirements,
  criteria: legacyCriteria,
  criteriaIndex,
  gateCriteria,
  scoredCriteria,
  qualityScore = 0,
  onCriterionClick,
  loading = false,
}: NormalizedCriteriaIndexProps) {
  // Merge V3 and legacy data
  const criteria = useMemo(() => {
    if (evaluationCriteria && evaluationCriteria.length > 0) {
      return evaluationCriteria;
    }
    if (legacyCriteria && legacyCriteria.length > 0) {
      return legacyCriteria;
    }
    // Convert legacy criteria if available
    const converted: RfpEvaluationCriterion[] = [];
    if (gateCriteria) {
      converted.push(...gateCriteria.map((c: any, idx: number) => ({
        id: c.id || `gate-${idx}`,
        title: c.title || c.name || 'Untitled',
        description: c.description || '',
        criterion_type: c.type || 'other',
        weight: c.weight,
        points: c.points,
        page: c.page || 0,
        position: 0,
        section_id: null,
        confidence: 0.8,
      })));
    }
    if (scoredCriteria) {
      converted.push(...scoredCriteria.map((c: any, idx: number) => ({
        id: c.id || `scored-${idx}`,
        title: c.title || c.name || 'Untitled',
        description: c.description || '',
        criterion_type: c.type || 'scored',
        weight: c.weight,
        points: c.points,
        page: c.page || 0,
        position: 0,
        section_id: null,
        confidence: 0.8,
      })));
    }
    return converted;
  }, [evaluationCriteria, legacyCriteria, gateCriteria, scoredCriteria]);

  const [searchQuery, setSearchQuery] = useState('');

  // Export functions
  const exportAsJSON = () => {
    const data = { criteria, requirements, qualityScore };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfp-criteria-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportAsCSV = () => {
    const csvRows = ['Type,Title,Description,Weight,Points,Page'];
    criteria.forEach(c => {
      const row = [
        c.criterion_type,
        `"${c.title}"`,
        `"${c.description}"`,
        c.weight || '',
        c.points || '',
        c.page || ''
      ].join(',');
      csvRows.push(row);
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfp-criteria-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const byType: Record<string, number> = {};
    let totalWeight = 0;
    let totalPoints = 0;
    let weightsCount = 0;
    let pointsCount = 0;

    criteria.forEach((c) => {
      byType[c.criterion_type] = (byType[c.criterion_type] || 0) + 1;
      if (c.weight !== null && c.weight !== undefined) {
        totalWeight += c.weight;
        weightsCount++;
      }
      if (c.points !== null && c.points !== undefined) {
        totalPoints += c.points;
        pointsCount++;
      }
    });

    return {
      total: criteria.length,
      byType,
      totalWeight: weightsCount > 0 ? totalWeight : null,
      totalPoints: pointsCount > 0 ? totalPoints : null,
      avgWeight: weightsCount > 0 ? totalWeight / weightsCount : null,
      avgPoints: pointsCount > 0 ? totalPoints / pointsCount : null,
    };
  }, [criteria]);

  // Filter criteria
  const filteredCriteria = useMemo(() => {
    let filtered = criteria;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(query) ||
          c.description.toLowerCase().includes(query) ||
          c.criterion_type.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [criteria, searchQuery]);

  if (loading) {
    return (
      <div className="w-full p-6 text-center">
        <p className="text-gray-500 dark:text-gray-400">Loading evaluation criteria...</p>
      </div>
    );
  }

  if (criteria.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          No evaluation criteria extracted. This may indicate that criteria are not clearly defined in the RFP, or
          extraction is pending.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Statistics */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">Evaluation Criteria</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {stats.total} criteria extracted
            {qualityScore > 0 && <span className="ml-2">• Quality: {qualityScore}/100</span>}
            {stats.totalWeight && <span className="ml-2">• Total Weight: {stats.totalWeight.toFixed(1)}%</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={exportAsJSON}>
            <Download className="h-4 w-4 mr-1" />
            JSON
          </Button>
          <Button size="sm" variant="outline" onClick={exportAsCSV}>
            <Download className="h-4 w-4 mr-1" />
            CSV
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search criteria..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Criteria List */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filteredCriteria.map((criterion) => (
          <Card
            key={criterion.id}
            className={`p-4 ${onCriterionClick ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800' : ''}`}
            onClick={() => onCriterionClick?.(criterion)}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-medium text-gray-900 dark:text-theme-text">{criterion.title}</h4>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                    {criterion.criterion_type}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{criterion.description}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 dark:text-gray-500">
                  {criterion.weight !== null && criterion.weight !== undefined && (
                    <span>Weight: {criterion.weight}%</span>
                  )}
                  {criterion.points !== null && criterion.points !== undefined && (
                    <span>Points: {criterion.points}</span>
                  )}
                  <span>Page: {criterion.page}</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {filteredCriteria.length === 0 && searchQuery && (
        <Card className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
            No criteria match your search. Try adjusting your query.
          </p>
        </Card>
      )}

      {/* Requirements Section */}
      {requirements && requirements.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text mb-3">Requirements</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {requirements.length} requirements identified
          </p>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {requirements.slice(0, 10).map((req) => (
              <Card key={req.id} className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                    {req.type}
                  </span>
                  <span className="text-xs text-gray-500">Page {req.page}</span>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">{req.content}</p>
              </Card>
            ))}
            {requirements.length > 10 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                ...and {requirements.length - 10} more requirements
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
